import {
	CreateOrderLines,
	Item,
	Order,
	UpdateOrderDto,
} from "@ang/shared-types";
import { CreateOrderDto } from "../generated-client";
import { User } from "../user";
/**
 * Order Model - Handles order-specific business logic and data manipulation
 */
export class OrderModel {
	createUpdateOrderDto(editOrderDTO: Order): UpdateOrderDto {
		return {
			docDueDate: editOrderDTO.docDueDate,
			comments: editOrderDTO.comments,
			documentLines: editOrderDTO.documentLines,
		};
	}
	duplicateOrder(oOrderData: Order) {
		const today = new Date();
		const tomorrow = new Date();
		tomorrow.setDate(today.getDate() + 1);

		return {
			docDate: today,
			docDueDate: tomorrow,
			docTotal: oOrderData.docTotal,
			documentLines: oOrderData.documentLines,
			user: oOrderData.user,
			comments: oOrderData.comments, //Maybe Empty?
			cardCode: oOrderData.cardCode,
		};
	}

	/**
	 * Removes a document line at the specified index
	 * @param documentLines Current document lines
	 * @param index Index to remove
	 * @returns New array with line removed
	 */
	removeDocumentLine(
		documentLines: CreateOrderLines[],
		index: number,
	): CreateOrderLines[] {
		if (!documentLines || index < 0 || index >= documentLines.length) {
			return documentLines || [];
		}

		const newLines = [...documentLines];
		newLines.splice(index, 1);
		return newLines;
	}

	/**
	 * Creates document lines from selected items
	 * @param selectedItems Selected items from dialog
	 * @param existingLines Existing document lines
	 * @returns New document lines array
	 */
	createDocumentLinesFromItems(
		selectedItems: any[],
		existingLines: CreateOrderLines[] = [],
	): CreateOrderLines[] {
		const newLines: CreateOrderLines[] = [];
		const profitCode = User.getInstance().getProfitCenter();
		console.log("Profit Center for new lines:", profitCode);

		selectedItems.forEach((oItem: any) => {
			const oContext = oItem.getBindingContext("itemsModel");
			const oItemData = oContext.getObject() as Item;

			const oExistingLine = existingLines?.find(
				(line) => line.itemCode === oItemData.itemCode,
			);

			if (oExistingLine) {
				newLines.push(oExistingLine);
			} else {
				newLines.push({
					itemCode: oItemData.itemCode,
					quantity: 1,
					profitCode: profitCode,
					...oItemData,
				} as CreateOrderLines);
			}
		});

		// Append new lines to existing lines, avoiding duplicates
		const existingItemCodes = existingLines.map((line) => line.itemCode);
		const appendedLines = [
			...existingLines,
			...newLines.filter((line) => !existingItemCodes.includes(line.itemCode)),
		];

		return appendedLines;
	}

	/**
	 * Creates a default order DTO with current date values
	 * @param username Current user's username
	 * @param cardCode Current user's card code
	 * @returns Default CreateOrderDto
	 */
	createDefaultOrderDto(username: string, cardCode: string): CreateOrderDto {
		const today = new Date();
		const tomorrow = new Date();
		tomorrow.setDate(today.getDate() + 1);

		return {
			docDate: today,
			docDueDate: tomorrow,
			docTotal: 0,
			documentLines: [],
			user: username || "unknown",
			comments: "",
			cardCode: cardCode || "unknown",
		};
	}

	/**
	 * Filters items based on a property value
	 * @param items All items
	 * @param propertyName Property to filter on
	 * @param expectedValue Expected value for the property
	 * @returns Filtered items
	 */
	filterItemsByProperty(
		items: Item[],
		propertyName: keyof Item,
		expectedValue: any,
	): Item[] {
		return items.filter((item) => item[propertyName] === expectedValue);
	}

	/**
	 * Gets existing item codes from document lines
	 * @param documentLines Current document lines
	 * @returns Array of item codes
	 */
	getExistingItemCodes(documentLines: CreateOrderLines[]): string[] {
		if (!documentLines || documentLines.length === 0) {
			return [];
		}
		return documentLines.map((line) => line.itemCode);
	}

	/**
	 * Checks if document lines are valid (not empty)
	 * @param documentLines Document lines to check
	 * @returns True if valid
	 */
	hasValidDocumentLines(documentLines: CreateOrderLines[]): boolean {
		return documentLines && documentLines.length > 0;
	}
}
